import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import FileUploadField from './FileUploadField';
import { authService } from '../../services/apiService';
import { fraudGateService } from '../../services/fraudGateService';
import { useAuth, USER_ROLES } from '../../auth/AuthContext';
import { getProfileDisplayEmail } from '../../utils/displayName';
import { FaUser, FaBuilding, FaUsers, FaStore, FaMapMarkerAlt, FaBox, FaFileUpload, FaUniversity, FaFileSignature } from 'react-icons/fa';
import { useI18n } from '../../contexts/I18nContext';
import LanguageSwitcher from '../LanguageSwitcher';

const STEPS = [
  { label: 'Business Identity', icon: FaStore, desc: 'Business basic information' },
  { label: 'Addresses', icon: FaMapMarkerAlt, desc: 'Business location details' },
  { label: 'Catalog', icon: FaBox, desc: 'Product catalog setup' },
  { label: 'KYC Uploads', icon: FaFileUpload, desc: 'Identity verification documents' },
  { label: 'Bank Details', icon: FaUniversity, desc: 'Banking information' },
  { label: 'Agreement', icon: FaFileSignature, desc: 'Terms and agreement' }
];
const DRAFT_KEY = 'onboarding_vendor_draft';

const Wrap = styled.div`
  height: 100vh;
  display: flex;
  background: #f0f4f8;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 380px;
  background: #1a2332;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 20% 30%, rgba(255,255,255,0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(255,255,255,0.03) 0%, transparent 50%);
    pointer-events: none;
    opacity: 0.6;
  }
  
  @media (max-width: 1024px) {
    width: 320px;
    padding: 32px 24px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
  
  .logo-s {
    width: 56px;
    height: 56px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .s-letter {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &::before {
        content: 'S';
        font-size: 48px;
        font-weight: 900;
        color: #ffffff;
        letter-spacing: -3px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
        text-shadow: 0 2px 8px rgba(0,0,0,0.4), 0 0 15px rgba(255,255,255,0.15);
        position: relative;
        z-index: 2;
      }
      
      &::after {
        content: '';
        position: absolute;
        width: 52px;
        height: 52px;
        border: 2px solid rgba(255,255,255,0.25);
        border-radius: 12px;
        transform: rotate(-10deg);
        z-index: 1;
        background: rgba(255,255,255,0.08);
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      }
    }
    
    .s-accent {
      position: absolute;
      width: 8px;
      height: 8px;
      background: rgba(255,255,255,0.7);
      border-radius: 50%;
      top: 8px;
      right: 8px;
      z-index: 3;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
    
    .s-decoration {
      position: absolute;
      width: 4px;
      height: 20px;
      background: rgba(255,255,255,0.3);
      border-radius: 2px;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%) rotate(15deg);
      z-index: 1;
    }
  }
`;

const ProgressSteps = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  z-index: 1;
`;

const ProgressStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  position: relative;
  padding-bottom: 32px;
  
  ${props => props.active && `
    .step-icon {
      background: #64b5f6;
      border-color: #ffffff;
      color: #ffffff;
      box-shadow: 0 2px 8px rgba(100, 181, 246, 0.3);
    }
    .step-label {
      color: #ffffff;
      font-weight: 600;
    }
    .step-desc {
      color: rgba(255,255,255,0.85);
    }
  `}
  
  ${props => !props.active && `
    .step-icon {
      background: rgba(255,255,255,0.08);
      border-color: rgba(255,255,255,0.25);
      color: rgba(255,255,255,0.6);
    }
    .step-label {
      color: rgba(255,255,255,0.65);
      font-weight: 500;
    }
    .step-desc {
      color: rgba(255,255,255,0.45);
    }
  `}
  
  ${props => props.isLast ? 'padding-bottom: 0;' : ''}
`;

const StepConnector = styled.div`
  position: absolute;
  left: 24px;
  top: 64px;
  width: 2px;
  height: 32px;
  background: ${props => props.completed ? '#64b5f6' : 'rgba(255,255,255,0.12)'};
  z-index: 0;
  transition: all 0.3s ease;
  
  &::after {
    content: '↓';
    position: absolute;
    bottom: -6px;
    left: -5px;
    width: 12px;
    height: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: ${props => props.completed ? '#64b5f6' : 'rgba(255,255,255,0.25)'};
    font-weight: bold;
    background: ${props => props.completed ? 'rgba(100, 181, 246, 0.15)' : 'rgba(255,255,255,0.08)'};
    border-radius: 50%;
    border: 1px solid ${props => props.completed ? 'rgba(100, 181, 246, 0.3)' : 'rgba(255,255,255,0.15)'};
  }
`;

const StepIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid;
  flex-shrink: 0;
  transition: all 0.3s ease;
  
  svg {
    font-size: 20px;
    filter: drop-shadow(0 1px 2px rgba(0,0,0,0.1));
  }
`;

const StepInfo = styled.div`
  flex: 1;
  padding-top: 4px;
`;

const StepLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
  transition: color 0.3s ease;
`;

const StepDesc = styled.div`
  font-size: 13px;
  transition: color 0.3s ease;
`;

const Copyright = styled.div`
  color: rgba(255,255,255,0.6);
  font-size: 12px;
  margin-top: auto;
  padding-top: 40px;
  position: relative;
  z-index: 1;
  font-weight: 400;
`;

const MainContent = styled.div`
  flex: 1;
  background: #f5f7fa;
  padding: 40px 60px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  height: 100vh;
  
  @media (max-width: 1200px) {
    padding: 32px 48px;
  }
  
  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
`;

const StepIndicator = styled.div`
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
  letter-spacing: 0.3px;
`;

const Title = styled.h1`
  font-size: 32px;
  color: #0f172a;
  margin: 0 0 10px 0;
  font-weight: 700;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const Sub = styled.p`
  color: #64748b;
  font-size: 15px;
  margin: 0 0 16px 0;
  line-height: 1.6;
`;

const StepContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
`;

const Section = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 32px 40px;
  width: 100%;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 100%;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  @media (max-width: 768px) {
    padding: 24px 20px;
    border-radius: 12px;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px 28px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Row2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 28px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Row3 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px 28px;
  margin-bottom: 20px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Field = styled.div`
  min-width: 0;
  max-width: 100%;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 10px;
  letter-spacing: 0.2px;
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  padding: 13px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  box-sizing: border-box;
  background: #ffffff;
  color: #0f172a;
  transition: all 0.2s ease;
  font-family: inherit;
  
  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
  
  &:hover {
    border-color: #cbd5e1;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
    background: #ffffff;
  }
`;

const Select = styled.select`
  width: 100%;
  min-width: 0;
  padding: 13px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  box-sizing: border-box;
  background: #ffffff;
  color: #0f172a;
  transition: all 0.2s ease;
  cursor: pointer;
  font-family: inherit;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23475569' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 40px;
  
  &:hover {
    border-color: #cbd5e1;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-width: 0;
  padding: 13px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  box-sizing: border-box;
  background: #ffffff;
  color: #0f172a;
  transition: all 0.2s ease;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  
  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
  
  &:hover {
    border-color: #cbd5e1;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
    background: #ffffff;
  }
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #3b82f6;
    border-radius: 4px;
  }
`;

const MobileInputWrapper = styled.div`
  display: flex;
  gap: 0;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s ease;
  background: #ffffff;
  
  &:hover {
    border-color: #cbd5e1;
  }
  
  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }
  
  .country-code {
    padding: 13px 16px;
    background: #f8fafc;
    border-right: 1.5px solid #e2e8f0;
    font-size: 15px;
    color: #0f172a;
    font-weight: 600;
    display: flex;
    align-items: center;
    white-space: nowrap;
  }
  
  input {
    flex: 1;
    border: none;
    padding: 13px 16px;
    font-size: 15px;
    background: #ffffff;
    color: #0f172a;
    font-family: inherit;
    
    &::placeholder {
      color: #94a3b8;
    }
    
    &:focus {
      outline: none;
    }
  }
`;

const BtnRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  gap: 12px;
  flex-shrink: 0;
  padding-top: 20px;
  border-top: 1.5px solid #e2e8f0;
  position: relative;
  z-index: 1;
`;

const Btn = styled.button`
  padding: 13px 32px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  font-size: 15px;
  border: none;
  transition: all 0.2s ease;
  letter-spacing: 0.3px;
  min-width: 120px;
  
  &.primary {
    background: #3b82f6;
    color: #ffffff;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  }
  
  &.primary:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
  
  &.primary:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  }
  
  &.secondary {
    background: #f8fafc;
    color: #475569;
    border: 1.5px solid #e2e8f0;
  }
  
  &.secondary:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
  
  &.secondary:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorP = styled.p`
  color: #dc2626;
  font-size: 14px;
  margin: 0 0 16px 0;
  font-weight: 500;
  position: relative;
  z-index: 1;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '⚠';
    font-size: 16px;
  }
`;

const defaultForm = {
  businessName: '',
  storeName: '',
  businessType: '',
  authorizedPerson: '',
  ownerMobile: '',
  email: '',
  gstNumber: '',
  panNumber: '',
  establishmentYear: '',
  businessDescription: '',
  businessLogo: '',
  // registered address split into components
  registeredArea: '',
  registeredCity: '',
  registeredState: '',
  registeredCountry: '',
  registeredPincode: '',
  warehouseSame: false,
  operatingCities: [],
  categories: [],
  minOrderValue: '',
  sameDayDispatch: false,
  returnPolicy: '',
  returnWindow: '',
  aadhaarFront: '',
  aadhaarBack: '',
  panUpload: '',
  gstCert: '',
  shopLicense: '',
  addressProof: '',
  bankName: '',
  accountHolderName: '',
  accountNumber: '',
  confirmAccountNumber: '',
  ifscCode: '',
  accountType: '',
  upiId: '',
  chequeUpload: '',
  agreementSigned: false,
  declaration: false,
  enableStore: true
};

export default function OnboardingVendor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useI18n();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(defaultForm);
  const [gstVerification, setGstVerification] = useState(null);
  const [bankVerification, setBankVerification] = useState(null);
  const [verifyingGst, setVerifyingGst] = useState(false);
  const [verifyingBank, setVerifyingBank] = useState(false);

  const loadDraft = useCallback(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        setForm((f) => ({ ...f, ...d.form })); if (d.step) setStep(d.step);
      }
    } catch (_) {}
  }, []);
  useEffect(() => { loadDraft(); }, [loadDraft]);
  useEffect(() => {
    if (!user) return;
    const displayEmail = getProfileDisplayEmail(user);
    const hasPhone = !!(user.phone && String(user.phone).trim());
    setForm((f) => ({
      ...f,
      email: displayEmail ? (f.email || displayEmail) : f.email,
      ownerMobile: hasPhone ? (f.ownerMobile || user.phone || '') : f.ownerMobile
    }));
  }, [user?.id, user?.email, user?.phone]);
  const saveDraft = useCallback(() => { localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, form })); }, [step, form]);
  useEffect(() => { const t = setInterval(saveDraft, 45000); return () => clearInterval(t); }, [saveDraft]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleVerifyGstin = async () => {
    if (!form.gstNumber?.trim() || !form.panNumber?.trim()) {
      setError('Enter GSTIN and PAN to verify');
      return;
    }
    setError('');
    setVerifyingGst(true);
    try {
      const result = await fraudGateService.verifyGstin({
        gstin: form.gstNumber.trim(),
        businessName: form.businessName || form.storeName,
        panNumber: form.panNumber.trim(),
        gstCertificateUrl: form.gstCert,
        panCardUrl: form.panUpload,
      });
      setGstVerification(result);
      if (result.status === 'verified') {
        setError('');
      } else if (result.status === 'manual_review') {
        setError(result.message || 'GSTIN queued for manual review (24h).');
      }
    } catch (err) {
      setGstVerification({ status: 'failed', message: err.response?.data?.message || err.message });
      setError(err.response?.data?.message || err.message || 'GSTIN verification failed');
    } finally {
      setVerifyingGst(false);
    }
  };

  const handleVerifyBank = async () => {
    if (!form.accountNumber?.trim() || !form.ifscCode?.trim() || !form.accountHolderName?.trim()) {
      setError('Complete bank details before verification');
      return;
    }
    if (form.accountNumber !== form.confirmAccountNumber) {
      setError('Account numbers do not match');
      return;
    }
    setError('');
    setVerifyingBank(true);
    try {
      const result = await fraudGateService.verifyBank({
        accountNumber: form.accountNumber.trim(),
        ifsc: form.ifscCode.trim(),
        accountHolderName: form.accountHolderName.trim(),
        businessName: form.businessName,
      });
      setBankVerification(result);
      if (result.status === 'manual_review') {
        setError(result.message || 'Bank name mismatch — ops will review.');
      }
    } catch (err) {
      const data = err.response?.data || {};
      setBankVerification({ status: data.payoutHeld ? 'payout_held' : 'failed', message: data.message });
      setError(data.message || err.message || 'Bank verification failed');
    } finally {
      setVerifyingBank(false);
    }
  };

  const verificationBadge = (status) => {
    if (!status) return null;
    const colors = {
      verified: { bg: '#ecfdf5', text: '#047857', label: 'Verified' },
      manual_review: { bg: '#fffbeb', text: '#b45309', label: 'Manual review' },
      payout_held: { bg: '#fef2f2', text: '#b91c1c', label: 'Payout held' },
      failed: { bg: '#fef2f2', text: '#b91c1c', label: 'Failed' },
    };
    const c = colors[status] || colors.manual_review;
    return (
      <span style={{ display: 'inline-block', marginTop: 8, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: c.bg, color: c.text }}>
        REG gate: {c.label}
      </span>
    );
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.businessName?.trim() || !form.storeName?.trim() || !form.panNumber?.trim()) {
      setError('Business name, store name and PAN are required');
      return;
    }
    setLoading(true);
    try {
      await authService.completeOnboarding({ role: USER_ROLES.VENDOR, vendorDetails: { ...form } });
      localStorage.removeItem(DRAFT_KEY);
      localStorage.setItem('userRole', USER_ROLES.VENDOR);
      navigate('/vendor/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const getStepTitle = () => {
    const titles = {
      1: t('onboarding.vendor.steps.businessIdentity'),
      2: t('onboarding.vendor.steps.addresses'),
      3: t('onboarding.vendor.steps.catalog'),
      4: t('onboarding.vendor.steps.kycUploads'),
      5: t('onboarding.vendor.steps.bankDetails'),
      6: t('onboarding.vendor.steps.agreement')
    };
    return titles[step] || t('onboarding.vendor.title');
  };

  const getStepDescription = () => {
    const descs = {
      1: t('onboarding.vendor.stepDescriptions.businessIdentity'),
      2: t('onboarding.vendor.stepDescriptions.addresses'),
      3: t('onboarding.vendor.stepDescriptions.catalog'),
      4: t('onboarding.vendor.stepDescriptions.kycUploads'),
      5: t('onboarding.vendor.stepDescriptions.bankDetails'),
      6: t('onboarding.vendor.stepDescriptions.agreement')
    };
    return descs[step] || '';
  };

  const getSteps = () => {
    return STEPS.map((stepItem, index) => ({
      ...stepItem,
      label: t(`onboarding.vendor.steps.${['businessIdentity', 'addresses', 'catalog', 'kycUploads', 'bankDetails', 'agreement'][index]}`),
      desc: t(`onboarding.vendor.stepDescriptions.${['businessIdentity', 'addresses', 'catalog', 'kycUploads', 'bankDetails', 'agreement'][index]}`)
    }));
  };

  return (
    <Wrap className="onboarding-light-page">
      <Sidebar>
        <Logo>
          <div className="logo-s">
            <div className="s-letter"></div>
            <div className="s-accent"></div>
            <div className="s-decoration"></div>
          </div>
        </Logo>
        <ProgressSteps>
          {getSteps().map((stepItem, index) => {
            const Icon = stepItem.icon;
            const isActive = step === index + 1;
            const isCompleted = step > index + 1;
            const isLast = index === STEPS.length - 1;
            return (
              <React.Fragment key={index}>
                <ProgressStep active={isActive} isLast={isLast}>
                  <StepIcon className="step-icon">
                    <Icon />
                  </StepIcon>
                  <StepInfo>
                    <StepLabel className="step-label">{stepItem.label}</StepLabel>
                    <StepDesc className="step-desc">{stepItem.desc}</StepDesc>
                  </StepInfo>
                  {!isLast && <StepConnector completed={isCompleted} />}
                </ProgressStep>
              </React.Fragment>
            );
          })}
        </ProgressSteps>
        <Copyright>All rights reserved @Sparepe</Copyright>
      </Sidebar>
      <MainContent>
        <HeaderSection>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <StepIndicator>{t('common.step')} {step}/{STEPS.length}</StepIndicator>
            </div>
            <LanguageSwitcher showInOnboarding={true} variant="minimal" />
          </div>
          <Title>{getStepTitle()}</Title>
          <Sub>{getStepDescription()}</Sub>
          {error && <ErrorP>{error}</ErrorP>}
        </HeaderSection>
        <StepContent>
        {step === 1 && (
          <Section>
            <Row>
              <Field>
                <Label>{t('onboarding.vendor.businessIdentity.businessName')} *</Label>
                <Input value={form.businessName} onChange={(e) => set('businessName', e.target.value)} placeholder={t('onboarding.vendor.businessIdentity.businessNamePlaceholder')} />
              </Field>
              <Field>
                <Label>Store Display Name *</Label>
                <Input value={form.storeName} onChange={(e) => set('storeName', e.target.value)} placeholder="Enter store name" />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>{t('onboarding.vendor.businessIdentity.businessType')} *</Label>
                <Select value={form.businessType} onChange={(e) => set('businessType', e.target.value)}>
                  <option value="">{t('onboarding.vendor.businessIdentity.selectBusinessType')}</option>
                  <option value="proprietorship">Proprietorship</option>
                  <option value="partnership">Partnership</option>
                  <option value="pvt_ltd">Pvt Ltd</option>
                </Select>
              </Field>
              <Field>
                <Label>Authorized Person *</Label>
                <Input value={form.authorizedPerson} onChange={(e) => set('authorizedPerson', e.target.value)} placeholder="Enter name" />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" />
              </Field>
              <Field>
                <Label>Mobile Number *</Label>
                <MobileInputWrapper>
                  <div className="country-code">+91</div>
                  <input 
                    type="tel" 
                    value={form.ownerMobile} 
                    onChange={(e) => set('ownerMobile', e.target.value.replace(/\D/g, '').slice(0, 10))} 
                    placeholder="10-digit mobile"
                  />
                </MobileInputWrapper>
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>{t('onboarding.vendor.businessIdentity.gstNumber')}</Label>
                <Input value={form.gstNumber} onChange={(e) => set('gstNumber', e.target.value)} placeholder={t('onboarding.vendor.businessIdentity.gstNumberPlaceholder')} />
              </Field>
              <Field>
                <Label>{t('onboarding.vendor.businessIdentity.panNumber')} *</Label>
                <Input value={form.panNumber} onChange={(e) => set('panNumber', e.target.value)} placeholder={t('onboarding.vendor.businessIdentity.panNumberPlaceholder')} />
              </Field>
            </Row>
            {form.gstNumber?.trim() && form.panNumber?.trim() && (
              <Row>
                <Field style={{ gridColumn: '1 / -1' }}>
                  <Btn
                    type="button"
                    className="secondary"
                    onClick={handleVerifyGstin}
                    disabled={verifyingGst}
                    style={{ maxWidth: 280 }}
                  >
                    {verifyingGst ? 'Verifying GSTIN…' : 'Verify GSTIN (REG-F1)'}
                  </Btn>
                  {verificationBadge(gstVerification?.status)}
                </Field>
              </Row>
            )}
            <Row>
              <Field>
                <Label>Establishment Year</Label>
                <Input value={form.establishmentYear} onChange={(e) => set('establishmentYear', e.target.value)} placeholder="YYYY" />
              </Field>
              <Field>
                <FileUploadField label="Business Logo" value={form.businessLogo} onChange={(v) => set('businessLogo', v)} accept="image/*" />
              </Field>
            </Row>
            <Row>
              <Field style={{ gridColumn: '1 / -1' }}>
                <Label>Business Description</Label>
                <Textarea value={form.businessDescription} onChange={(e) => set('businessDescription', e.target.value)} placeholder="Brief description of your business" rows={4} />
              </Field>
            </Row>
          </Section>
        )}
        {step === 2 && (
          <Section>
            <Row>
              <Field>
                <Label>Area / Locality *</Label>
                <Input value={form.registeredArea} onChange={(e) => set('registeredArea', e.target.value)} placeholder="Area / locality" />
              </Field>
              <Field>
                <Label>City *</Label>
                <Input value={form.registeredCity} onChange={(e) => set('registeredCity', e.target.value)} placeholder="City" />
              </Field>
              <Field>
                <Label>State *</Label>
                <Input value={form.registeredState} onChange={(e) => set('registeredState', e.target.value)} placeholder="State" />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>Country</Label>
                <Input value={form.registeredCountry} onChange={(e) => set('registeredCountry', e.target.value)} placeholder="Country" />
              </Field>
              <Field>
                <Label>Pincode *</Label>
                <Input value={form.registeredPincode} onChange={(e) => set('registeredPincode', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Pincode" />
              </Field>
            </Row>
            <Row>
              <Field>
                <CheckboxWrapper>
                  <input type="checkbox" checked={form.warehouseSame} onChange={(e) => set('warehouseSame', e.target.checked)} />
                  <span>Warehouse same as registered</span>
                </CheckboxWrapper>
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>Operating Cities (comma-separated)</Label>
                <Input value={Array.isArray(form.operatingCities) ? form.operatingCities.join(', ') : (form.operatingCities || '')} onChange={(e) => set('operatingCities', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} placeholder="City1, City2, City3" />
              </Field>
            </Row>
          </Section>
        )}
        {step === 3 && (
          <Section>
            <Row>
              <Field>
                <Label>Min Order Value</Label>
                <Input value={form.minOrderValue} onChange={(e) => set('minOrderValue', e.target.value)} placeholder="Enter amount" />
              </Field>
              <Field>
                <CheckboxWrapper>
                  <input type="checkbox" checked={form.sameDayDispatch} onChange={(e) => set('sameDayDispatch', e.target.checked)} />
                  <span>Same Day Dispatch</span>
                </CheckboxWrapper>
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>Return Policy *</Label>
                <Select value={form.returnPolicy} onChange={(e) => set('returnPolicy', e.target.value)}>
                  <option value="">Select</option>
                  <option value="no_returns">No returns</option>
                  <option value="conditional">Conditional</option>
                </Select>
              </Field>
              <Field>
                <Label>Return Window (days)</Label>
                <Input value={form.returnWindow} onChange={(e) => set('returnWindow', e.target.value)} placeholder="Enter days" />
              </Field>
            </Row>
          </Section>
        )}
        {step === 4 && (
          <Section>
            <Row2>
              <Field>
                <FileUploadField label="Aadhaar Front" value={form.aadhaarFront} onChange={(v) => set('aadhaarFront', v)} accept="image/*,.pdf" />
              </Field>
              <Field>
                <FileUploadField label="Aadhaar Back" value={form.aadhaarBack} onChange={(v) => set('aadhaarBack', v)} accept="image/*,.pdf" />
              </Field>
            </Row2>
            <Row2>
              <Field>
                <FileUploadField label="PAN Document *" value={form.panUpload} onChange={(v) => set('panUpload', v)} accept="image/*,.pdf" required />
              </Field>
              <Field>
                <FileUploadField label="GST Certificate" value={form.gstCert} onChange={(v) => set('gstCert', v)} accept="image/*,.pdf" />
              </Field>
            </Row2>
            <Row2>
              <Field>
                <FileUploadField label="Shop License" value={form.shopLicense} onChange={(v) => set('shopLicense', v)} accept="image/*,.pdf" />
              </Field>
              <Field>
                <FileUploadField label="Address Proof" value={form.addressProof} onChange={(v) => set('addressProof', v)} accept="image/*,.pdf" />
              </Field>
            </Row2>
          </Section>
        )}
        {step === 5 && (
          <Section>
            <Row>
              <Field>
                <Label>Bank Name *</Label>
                <Input value={form.bankName} onChange={(e) => set('bankName', e.target.value)} placeholder="Enter bank name" />
              </Field>
              <Field>
                <Label>Account Holder Name *</Label>
                <Input value={form.accountHolderName} onChange={(e) => set('accountHolderName', e.target.value)} placeholder="Enter name" />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>Account Number *</Label>
                <Input value={form.accountNumber} onChange={(e) => set('accountNumber', e.target.value)} placeholder="Enter account number" />
              </Field>
              <Field>
                <Label>Confirm Account Number *</Label>
                <Input value={form.confirmAccountNumber} onChange={(e) => set('confirmAccountNumber', e.target.value)} placeholder="Re-enter account number" />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>IFSC Code *</Label>
                <Input value={form.ifscCode} onChange={(e) => set('ifscCode', e.target.value)} placeholder="Enter IFSC code" />
              </Field>
              <Field>
                <Label>Account Type</Label>
                <Select value={form.accountType} onChange={(e) => set('accountType', e.target.value)}>
                  <option value="">Select</option>
                  <option value="savings">Savings</option>
                  <option value="current">Current</option>
                </Select>
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>UPI ID</Label>
                <Input value={form.upiId} onChange={(e) => set('upiId', e.target.value)} placeholder="Optional" />
              </Field>
              <Field>
                <FileUploadField label="Cancelled Cheque *" value={form.chequeUpload} onChange={(v) => set('chequeUpload', v)} accept="image/*,.pdf" required />
              </Field>
            </Row>
            <Row>
              <Field style={{ gridColumn: '1 / -1' }}>
                <Btn
                  type="button"
                  className="secondary"
                  onClick={handleVerifyBank}
                  disabled={verifyingBank}
                  style={{ maxWidth: 300 }}
                >
                  {verifyingBank ? 'Sending ₹1 penny drop…' : 'Verify bank account (REG-F2)'}
                </Btn>
                {verificationBadge(bankVerification?.status)}
                <p style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>
                  Penny drop confirms account is active. Name must match your business name (70%+ match).
                </p>
              </Field>
            </Row>
          </Section>
        )}
        {step === 6 && (
          <Section>
            <Row>
              <Field>
                <Label>
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', padding: 0, color: '#3b82f6', textDecoration: 'none', fontWeight: 600, cursor: 'pointer', font: 'inherit' }}
                  >
                    Download Vendor Agreement
                  </button>
                </Label>
              </Field>
            </Row>
            <Row>
              <Field>
                <CheckboxWrapper>
                  <input type="checkbox" checked={form.declaration} onChange={(e) => set('declaration', e.target.checked)} />
                  <span>Declaration *</span>
                </CheckboxWrapper>
              </Field>
            </Row>
            <Row>
              <Field>
                <CheckboxWrapper>
                  <input type="checkbox" checked={form.enableStore} onChange={(e) => set('enableStore', e.target.checked)} />
                  <span>Enable Store Listing</span>
                </CheckboxWrapper>
              </Field>
            </Row>
          </Section>
        )}
        </StepContent>
        <BtnRow>
          <Btn type="button" className="secondary" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}>{t('common.previous')}</Btn>
          {step < STEPS.length ? (
            <Btn type="button" className="primary" onClick={() => { saveDraft(); setStep((s) => s + 1); }}>{t('common.next')}</Btn>
          ) : (
            <Btn type="button" className="primary" onClick={handleSubmit} disabled={loading}>{loading ? t('common.loading') : t('onboarding.vendor.agreement.submit')}</Btn>
          )}
        </BtnRow>
      </MainContent>
    </Wrap>
  );
}
